import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone_number: '',
    verification_method: 'email',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setPendingVerificationEmail('');
    setLoading(true);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    // Validasi
    if (formData.password !== formData.confirmPassword) {
      setError('Password tidak cocok');
      setLoading(false);
      clearTimeout(timeoutId);
      return;
    }

    if (formData.verification_method === 'whatsapp' && !formData.phone_number) {
      setError('Nomor WhatsApp harus diisi untuk verifikasi WhatsApp');
      setLoading(false);
      clearTimeout(timeoutId);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password: formData.password,
          phone_number: formData.phone_number,
          verification_method: formData.verification_method,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.error || 'Registrasi gagal';
        const isUnverifiedEmailCase =
          response.status === 409 &&
          (data.code === 'EMAIL_EXISTS_UNVERIFIED' ||
            errorMessage.toLowerCase().includes('belum diverifikasi'));

        if (isUnverifiedEmailCase) {
          setPendingVerificationEmail(formData.email);
          setError(errorMessage);
          navigate('/verify-email', { state: { email: formData.email } });
          return;
        }

        setError(errorMessage);
        return;
      }

      setSuccess(data.message);
      
      // Redirect ke verification page setelah 2 detik
      setTimeout(() => {
        if (formData.verification_method === 'email') {
          navigate('/verify-email', { state: { email: formData.email } });
        } else if (formData.verification_method === 'whatsapp') {
          navigate('/verify-whatsapp', { state: { phone_number: formData.phone_number } });
        } else {
          navigate('/login');
        }
      }, 2000);
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Request timeout. Coba lagi dalam beberapa detik.');
      } else {
        setError('Error: ' + err.message);
      }
    } finally {
      setLoading(false);
      clearTimeout(timeoutId);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md">
        <h1 className="text-3xl font-bold text-center mb-2 text-blue-600">Yayasan BCN</h1>
        <h2 className="text-2xl font-bold mb-6 text-center">Daftar Akun Baru</h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}

        <form onSubmit={handleRegister} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Username</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Username (minimal 3 karakter)"
              minLength="3"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Alamat email"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Password (minimal 6 karakter)"
                minLength="6"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-900 focus:outline-none text-xl"
                title={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
              >
                {showPassword ? '🔓' : '🔒'}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Konfirmasi Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Konfirmasi password"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-900 focus:outline-none text-xl"
                title={showConfirmPassword ? 'Sembunyikan password' : 'Tampilkan password'}
              >
                {showConfirmPassword ? '🔓' : '🔒'}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Nomor WhatsApp (Opsional)</label>
            <input
              type="tel"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="+62812345678"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Metode Verifikasi</label>
            <select
              name="verification_method"
              value={formData.verification_method}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="email">Email</option>
              <option value="whatsapp">WhatsApp (jika nomor diisi)</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 font-semibold"
          >
            {loading ? 'Sedang mendaftar...' : 'Daftar'}
          </button>
        </form>

        {pendingVerificationEmail && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            <p className="mb-3">
              Email ini sudah terdaftar tetapi belum diverifikasi.
            </p>
            <button
              type="button"
              onClick={() => navigate('/verify-email', { state: { email: pendingVerificationEmail } })}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-semibold"
            >
              Buka halaman verifikasi email
            </button>
          </div>
        )}

        <p className="text-center mt-4">
          Sudah punya akun?{' '}
          <Link to="/login" className="text-blue-600 hover:underline font-semibold">
            Login di sini
          </Link>
        </p>

        <p className="text-xs text-gray-500 text-center mt-6">
          Dengan mendaftar, Anda setuju dengan syarat dan ketentuan kami
        </p>
      </div>
    </div>
  );
};

export default Register;
